/**
 * IndexedDB管理クラス
 * CreaNizerの永続化データを管理
 */

import { CustomValuesManager } from './managers/CustomValuesManager'
import { ProjectsManager } from './managers/ProjectsManager'
import { ApiKeysManager } from './managers/ApiKeysManager'
import { SettingsManager } from './managers/SettingsManager'
import { ImagePromptsManager } from './managers/ImagePromptsManager'
import type { CustomValue, StoredProject, ApiKey, SavedImagePrompt } from './managers/types'
import { getApiKeyProvider } from './managers/types'
import type { AIProviderId } from '@creanizer/types'

// 型の再エクスポート（後方互換性のため）
export type { CustomValue, StoredProject, ApiKey, SavedImagePrompt } from './managers/types'
export { getApiKeyProvider } from './managers/types'

// Browser API check helper
const isBrowser = typeof globalThis !== 'undefined' && 
                  typeof (globalThis as any).window !== 'undefined' &&
                  typeof (globalThis as any).indexedDB !== 'undefined'

class IndexedDBManager {
  private db: any | null = null
  private readonly dbName = 'CreaNizerDB'
  private readonly version = 4  // v4に統一
  private useLocalStorageFallback = false
  
  // 各マネージャーのインスタンス
  private customValuesManager: CustomValuesManager | null = null
  private projectsManager: ProjectsManager | null = null
  private apiKeysManager: ApiKeysManager | null = null
  private settingsManager: SettingsManager | null = null
  private imagePromptsManager: ImagePromptsManager | null = null

  async init(): Promise<void> {
    // IndexedDBが使えるか確認
    if (!isBrowser) {
      console.warn('IndexedDB not supported, using localStorage fallback')
      this.useLocalStorageFallback = true
      return
    }

    return new Promise((resolve, reject) => {
      try {
        const request = (globalThis as any).indexedDB.open(this.dbName, this.version)

        request.onerror = () => {
          console.error('IndexedDB error:', request.error)
          this.useLocalStorageFallback = true
          resolve() // エラーでもresolveして処理を続行
        }

        request.onsuccess = () => {
          this.db = request.result
          // マネージャーのインスタンス化
          this.customValuesManager = new CustomValuesManager(this.db)
          this.projectsManager = new ProjectsManager(this.db)
          this.apiKeysManager = new ApiKeysManager(this.db)
          this.settingsManager = new SettingsManager(this.db)
          this.imagePromptsManager = new ImagePromptsManager(this.db)
          resolve()
        }

        request.onupgradeneeded = (event: any) => {
          const db = (event.target as any).result
          const oldVersion = event.oldVersion
          
          console.log(`Upgrading IndexedDB from version ${oldVersion} to ${this.version}`)

          // カスタム値ストア
          if (!db.objectStoreNames.contains('customValues')) {
            const customValuesStore = db.createObjectStore('customValues', { keyPath: 'id' })
            customValuesStore.createIndex('type', 'type', { unique: false })
            customValuesStore.createIndex('nameType', 'nameType', { unique: false })
            customValuesStore.createIndex('usageCount', 'usageCount', { unique: false })
          }

          // プロジェクトストア
          if (!db.objectStoreNames.contains('projects')) {
            const projectsStore = db.createObjectStore('projects', { keyPath: 'id' })
            projectsStore.createIndex('updatedAt', 'updatedAt', { unique: false })
            projectsStore.createIndex('name', 'name', { unique: false })
          }

          // 設定ストア
          if (!db.objectStoreNames.contains('settings')) {
            db.createObjectStore('settings', { keyPath: 'key' })
          }

          // APIキーストア
          if (!db.objectStoreNames.contains('apiKeys')) {
            const apiKeysStore = db.createObjectStore('apiKeys', { keyPath: 'id' })
            apiKeysStore.createIndex('isActive', 'isActive', { unique: false })
            apiKeysStore.createIndex('createdAt', 'createdAt', { unique: false })
          }

          // 画像プロンプトストア（バージョン3で追加）
          if (!db.objectStoreNames.contains('imagePrompts')) {
            const imagePromptsStore = db.createObjectStore('imagePrompts', { keyPath: 'id' })
            imagePromptsStore.createIndex('createdAt', 'createdAt', { unique: false })
            imagePromptsStore.createIndex('title', 'title', { unique: false })
          }
          
          // v4で追加: zustandStoresストア（すでに作成済みだが念のため）
          if (!db.objectStoreNames.contains('zustandStores')) {
            db.createObjectStore('zustandStores', { keyPath: 'key' })
            console.log('Created zustandStores object store')
          }

          // v4で追加: マイグレーションログストア（すでに作成済みだが念のため）
          if (!db.objectStoreNames.contains('migrationLogs')) {
            const logStore = db.createObjectStore('migrationLogs', { 
              keyPath: 'id', 
              autoIncrement: true 
            })
            logStore.createIndex('storeName', 'storeName', { unique: false })
            logStore.createIndex('timestamp', 'timestamp', { unique: false })
            console.log('Created migrationLogs object store')
          }
        }
      } catch (error) {
        console.error('Failed to open IndexedDB:', error)
        this.useLocalStorageFallback = true
        resolve()
      }
    })
  }

  // カスタム値の操作（CustomValuesManagerに委譲）
  async addCustomValue(customValue: Omit<CustomValue, 'id' | 'createdAt' | 'usageCount'>): Promise<CustomValue> {
    if (!this.customValuesManager) await this.init()
    return this.customValuesManager!.addCustomValue(customValue)
  }

  async getCustomValues(type?: CustomValue['type'], nameType?: string): Promise<CustomValue[]> {
    if (!this.customValuesManager) await this.init()
    return this.customValuesManager!.getCustomValues(type, nameType)
  }

  async updateCustomValueUsage(id: string): Promise<void> {
    if (!this.customValuesManager) await this.init()
    return this.customValuesManager!.updateCustomValueUsage(id)
  }

  async deleteCustomValue(id: string): Promise<void> {
    if (!this.customValuesManager) await this.init()
    return this.customValuesManager!.deleteCustomValue(id)
  }

  // プロジェクトの操作（ProjectsManagerに委譲）
  async saveProject(project: Omit<StoredProject, 'id' | 'createdAt' | 'updatedAt'>): Promise<StoredProject> {
    if (!this.projectsManager) await this.init()
    return this.projectsManager!.saveProject(project)
  }

  async getProjects(): Promise<StoredProject[]> {
    if (!this.projectsManager) await this.init()
    return this.projectsManager!.getProjects()
  }

  async updateProject(id: string, data: any): Promise<void> {
    if (!this.projectsManager) await this.init()
    return this.projectsManager!.updateProject(id, data)
  }

  async deleteProject(id: string): Promise<void> {
    if (!this.projectsManager) await this.init()
    return this.projectsManager!.deleteProject(id)
  }

  // 設定の操作（SettingsManagerに委譲）
  async setSetting(key: string, value: any): Promise<void> {
    if (!this.settingsManager) await this.init()
    return this.settingsManager!.setSetting(key, value)
  }

  async getSetting(key: string): Promise<any> {
    if (!this.settingsManager) await this.init()
    return this.settingsManager!.getSetting(key)
  }

  // APIキーの操作（ApiKeysManagerに委譲）
  async addApiKey(apiKey: Omit<ApiKey, 'id' | 'createdAt' | 'usageCount'>): Promise<ApiKey> {
    if (this.useLocalStorageFallback) {
      // LocalStorageフォールバック
      const newApiKey: ApiKey = {
        ...apiKey,
        id: crypto.randomUUID(),
        createdAt: new Date(),
        usageCount: 0,
      }
      const keys = this.getLocalStorageApiKeys()
      keys.push(newApiKey)
      if (isBrowser) (globalThis as any).localStorage.setItem('creanizer-apikeys', JSON.stringify(keys))
      return newApiKey
    }
    if (!this.apiKeysManager) await this.init()
    return this.apiKeysManager!.addApiKey(apiKey)
  }

  async getApiKeys(): Promise<ApiKey[]> {
    if (this.useLocalStorageFallback) {
      return this.getLocalStorageApiKeys()
    }
    if (!this.apiKeysManager) await this.init()
    return this.apiKeysManager!.getApiKeys()
  }

  async getActiveApiKey(provider: AIProviderId = 'gemini'): Promise<ApiKey | null> {
    if (this.useLocalStorageFallback) {
      const keys = this.getLocalStorageApiKeys()
      return keys.find(k =>
        k.isActive && getApiKeyProvider(k) === provider
      ) || null
    }
    if (!this.apiKeysManager) await this.init()
    return this.apiKeysManager!.getActiveApiKey(provider)
  }

  private getLocalStorageApiKeys(): ApiKey[] {
    if (!isBrowser) return []
    try {
      const stored = (globalThis as any).localStorage.getItem('creanizer-apikeys')
      if (stored) {
        return JSON.parse(stored)
      }
    } catch (error) {
      console.error('Failed to read from localStorage:', error)
    }
    return []
  }

  async updateApiKey(id: string, updates: Partial<ApiKey>): Promise<void> {
    if (this.useLocalStorageFallback) {
      const keys = this.getLocalStorageApiKeys()
      const index = keys.findIndex(k => k.id === id)
      if (index !== -1) {
        keys[index] = { ...keys[index], ...updates }
        if (isBrowser) (globalThis as any).localStorage.setItem('creanizer-apikeys', JSON.stringify(keys))
      }
      return
    }
    if (!this.apiKeysManager) await this.init()
    return this.apiKeysManager!.updateApiKey(id, updates)
  }

  async setActiveApiKey(id: string): Promise<void> {
    if (this.useLocalStorageFallback) {
      const keys = this.getLocalStorageApiKeys()
      const targetKey = keys.find(key => key.id === id)
      if (!targetKey) throw new Error('API key not found')
      const targetProvider = getApiKeyProvider(targetKey)
      const updatedKeys = keys.map(key => getApiKeyProvider(key) === targetProvider
        ? { ...key, isActive: key.id === id }
        : key
      )
      if (isBrowser) (globalThis as any).localStorage.setItem('creanizer-apikeys', JSON.stringify(updatedKeys))
      return
    }
    if (!this.apiKeysManager) await this.init()
    return this.apiKeysManager!.setActiveApiKey(id)
  }

  async deleteApiKey(id: string): Promise<void> {
    if (this.useLocalStorageFallback) {
      const keys = this.getLocalStorageApiKeys()
      const filteredKeys = keys.filter(k => k.id !== id)
      if (isBrowser) (globalThis as any).localStorage.setItem('creanizer-apikeys', JSON.stringify(filteredKeys))
      return
    }
    if (!this.apiKeysManager) await this.init()
    return this.apiKeysManager!.deleteApiKey(id)
  }

  async incrementApiKeyUsage(id: string): Promise<void> {
    if (this.useLocalStorageFallback) {
      const keys = this.getLocalStorageApiKeys()
      const index = keys.findIndex(k => k.id === id)
      if (index !== -1) {
        keys[index].usageCount++
        keys[index].lastUsedAt = new Date()
        if (isBrowser) (globalThis as any).localStorage.setItem('creanizer-apikeys', JSON.stringify(keys))
      }
      return
    }
    if (!this.apiKeysManager) await this.init()
    return this.apiKeysManager!.incrementApiKeyUsage(id)
  }

  // IndexedDB利用可否を確認
  isUsingFallback(): boolean {
    return this.useLocalStorageFallback
  }

  // 画像プロンプトの操作（ImagePromptsManagerに委譲）
  async saveImagePrompt(prompt: SavedImagePrompt): Promise<void> {
    if (!this.imagePromptsManager) await this.init()
    return this.imagePromptsManager!.saveImagePrompt(prompt)
  }

  async getImagePrompt(id: string): Promise<SavedImagePrompt | undefined> {
    if (!this.imagePromptsManager) await this.init()
    return this.imagePromptsManager!.getImagePrompt(id)
  }

  async getAllImagePrompts(): Promise<SavedImagePrompt[]> {
    if (!this.imagePromptsManager) await this.init()
    return this.imagePromptsManager!.getAllImagePrompts()
  }

  async updateImagePrompt(id: string, updates: Partial<SavedImagePrompt>): Promise<void> {
    if (!this.imagePromptsManager) await this.init()
    return this.imagePromptsManager!.updateImagePrompt(id, updates)
  }

  async deleteImagePrompt(id: string): Promise<void> {
    if (!this.imagePromptsManager) await this.init()
    return this.imagePromptsManager!.deleteImagePrompt(id)
  }

  async deleteAllImagePrompts(): Promise<void> {
    if (!this.imagePromptsManager) await this.init()
    return this.imagePromptsManager!.deleteAllImagePrompts()
  }

  // データのエクスポート/インポート
  async exportData(): Promise<any> {
    if (!this.db) await this.init()

    const customValues = await this.getCustomValues()
    const projects = await this.getProjects()
    const apiKeys = await this.getApiKeys()
    const imagePrompts = await this.getAllImagePrompts()
    
    const settings: Record<string, any> = {}
    const settingsPromise = new Promise((resolve) => {
      const transaction = this.db!.transaction(['settings'], 'readonly')
      const store = transaction.objectStore('settings')
      const request = store.getAll()
      
      request.onsuccess = () => {
        request.result.forEach((item: any) => {
          settings[item.key] = item.value
        })
        resolve(settings)
      }
    })
    
    await settingsPromise

    return {
      version: this.version,
      exportedAt: new Date().toISOString(),
      customValues,
      projects,
      apiKeys,
      imagePrompts,
      settings,
    }
  }

  async importData(data: any): Promise<void> {
    if (!this.db) await this.init()

    // カスタム値のインポート
    if (data.customValues) {
      for (const value of data.customValues) {
        await this.addCustomValue(value)
      }
    }

    // プロジェクトのインポート
    if (data.projects) {
      for (const project of data.projects) {
        await this.saveProject(project)
      }
    }

    // APIキーのインポート
    if (data.apiKeys) {
      for (const apiKey of data.apiKeys) {
        await this.addApiKey(apiKey)
      }
    }

    // 画像プロンプトのインポート
    if (data.imagePrompts) {
      for (const prompt of data.imagePrompts) {
        await this.saveImagePrompt(prompt)
      }
    }

    // 設定のインポート
    if (data.settings) {
      for (const [key, value] of Object.entries(data.settings)) {
        await this.setSetting(key, value)
      }
    }
  }
}

// シングルトンインスタンスをエクスポート
export const indexedDBManager = new IndexedDBManager()
